import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { CSSTransitionGroup } from "react-transition-group";
import { Alert } from "reactstrap";
import { orderBy, uniq, update } from "lodash";
import moment from "moment";
import __ from "src/utils/Translations";
import Form from "src/Components/Form/index";
import { LAYOUT_TWO_COLUMNS } from "src/Components/Layouts";
import ContentLoading from "src/Components/Loading/contentLoading";
import PageTitle from "src/Layout/AppMain/PageTitle";
import { API_TOTALREWARD_SERVICE } from "src/utils/Api";
import { dynamicNotification } from "src/utils/Notifications";
import { getCompanyBaseBreadcrumbs } from "src/Pages/Company/routerHelper";
import { useCompanyId, useCompanyName } from "src/Pages/Company/CompanyContext";
import {
  useGetTotalRewardsPerk,
  useSaveTotalRewardsPerk,
} from "src/Pages/Company/TotalReward/api/usePerks";
import { useListTotalRewardsPerksCategoriesOptions } from "src/Pages/Company/TotalReward/api/usePerksCategories";
import useCompanySubscriptions from "src/Pages/Company/TotalReward/api/useCompanySubscriptions";
import useCompanyPointsBanks from "src/Pages/Company/TotalReward/api/useCompanyPointsBanks";
import useCompanyBenefits from "src/Pages/Company/TotalReward/api/useCompanyBenefits";
import useEmployeeGroups from "src/utils/hooks/company/useEmployeeGroups";
import useRentableGroups from "src/utils/hooks/company/useRentableGroups";
import useOrganizationUnits from "src/utils/hooks/company/useOrganizationUnits";
import translateLabels from "src/utils/Translations/translateLabels";
import useHasPermission from "src/utils/hooks/security/useHasPermission";
import {
  totalRewardPerksAutomationWrite,
  totalRewardPerksWrite,
} from "src/utils/RoleBasedSecurity/permissions";
import useIsImpersonated from "src/utils/hooks/useIsImpersonated";

const fileReader = new FileReader();

export default function TotalRewardPerksForm({
  perkId,
  pathPrefix,
  yourBenefitsEnabled,
  availableBenefitsEnabled,
  enabledImportPointsBanks,
  enabledPointsBanks,
  enabledImportBenefits,
  enabledBenefits,
  setIsEdited,
}) {
  const isImpersonated = useIsImpersonated();
  const listingUrl = `${pathPrefix}/total-reward/perks`;
  const history = useHistory();
  const isNew = perkId === "-1";
  const companyId = useCompanyId();

  const hasAutomationWrite = useHasPermission(totalRewardPerksAutomationWrite);
  const automationEnabled =
    hasAutomationWrite && (enabledImportPointsBanks || enabledImportBenefits);

  const [fileDataURL, setFileDataURL] = useState(null);
  const [coverImgInfo, setCoverImgInfo] = useState([]);

  const onImgLoad = ({ target: img }) => {
    const gcd = (...arr) => {
      const gcd2 = (x, y) => (!y ? x : gcd(y, x % y));
      return [...arr].reduce((a, b) => gcd2(a, b));
    };

    const gcdResult = gcd(img.naturalWidth, img.naturalHeight);

    setCoverImgInfo([
      { title: __("Wysokość"), value: `${img.naturalHeight}px` },
      { title: __("Szerokość"), value: `${img.naturalWidth}px` },
      {
        title: __("Proporcje"),
        value: `${img.naturalWidth / gcdResult}:${
          img.naturalHeight / gcdResult
        }`,
      },
      {
        title: __("Orientacja"),
        value:
          img.naturalHeight <= img.naturalWidth ? __("pozioma") : __("pionowa"),
      },
    ]);
  };

  const employeeGroups = useEmployeeGroups(
    true,
    "companyId",
    companyId,
    false,
    !(companyId && availableBenefitsEnabled)
  );
  const rentableGroups = useRentableGroups(
    true,
    "companyId",
    companyId,
    false,
    !(companyId && availableBenefitsEnabled)
  );
  const organizationUnits = useOrganizationUnits(
    true,
    "companyId",
    companyId,
    false,
    !(companyId && availableBenefitsEnabled)
  );
  const { data: pointsBanks } = useCompanyPointsBanks(
    companyId,
    !!automationEnabled,
    true
  );
  const { data: benefits } = useCompanyBenefits(
    companyId,
    !!automationEnabled,
    true
  );
  const { data: subscriptions } = useCompanySubscriptions(
    companyId,
    !!automationEnabled,
    true
  );

  const allBenefits = useMemo(
    () =>
      orderBy(uniq([...(benefits || []), ...(subscriptions || [])]), "label"),
    [benefits, subscriptions]
  );

  const [isDirty, setIsDirty] = useState(false);
  const [originData, setOriginData] = useState();
  const [data, setData] = useState({
    visibility: !!availableBenefitsEnabled,
    status: !!yourBenefitsEnabled,
    activeFrom: null,
    activeTo: null,
    value: false,
    videos: [],
    files: [],
    employeeGroups: [],
    rentableGroups: [],
    organizationUnits: [],
    pointBanks: [],
    perkSubscriptions: [],
    isAutomatic: false,
    isFinancial: false,
    sumFormat: null,
    sort: 0,
  });

  const {
    data: perk,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useGetTotalRewardsPerk(perkId);

  useEffect(() => {
    if (isSuccess) {
      setOriginData(perk);

      let sourceType = null;
      if (perk.perkSubscriptions?.length > 0) {
        sourceType = "benefits";
      }
      if (perk.pointBanks?.length > 0) {
        sourceType = "pointsBanks";
      }

      setData({
        sort: 0,
        videos: [],
        ...perk,
        sumFormat: String(perk.sumFormat || 0),
        employeeGroups: uniq(perk.employeeGroups),
        rentableGroups: uniq(perk.rentableGroups),
        organizationUnits: uniq(perk.organizationUnits),
        files:
          (perk?.files || []).map((f) => ({
            ...f,
            title: f.fileTitle,
            file: null,
          })) || [],
        image: null,
        category: perk.category?.id
          ? `/omb/perk-categories/${perk.category.id}`
          : null,
        sourceType,
        // virtual field
        isFinancial: perk.isAutomatic || perk.sumFormat > 2,
      });
    }
  }, [isSuccess, perk]);

  useEffect(() => {
    if (isError) {
      setErrorMessage(error?.message);
    }
  }, [isError, error?.message]);

  const { data: perkCategoriesOptions } =
    useListTotalRewardsPerksCategoriesOptions(companyId);

  const hiddenEmployeeGroups =
    originData?.employeeGroups.filter(
      (id) => !employeeGroups.map((g) => g.value).includes(id)
    ) || [];
  const hiddenRentableGroups =
    originData?.rentableGroups.filter(
      (id) => !rentableGroups.map((g) => g.value).includes(id)
    ) || [];
  const hiddenOrganizationUnits =
    originData?.organizationUnits.filter(
      (id) => !organizationUnits.map((g) => g.value).includes(id)
    ) || [];

  const anyGroupSelected =
    data.employeeGroups?.length > 0 ||
    hiddenEmployeeGroups.length > 0 ||
    data.rentableGroups?.length > 0 ||
    hiddenRentableGroups.length > 0 ||
    data.organizationUnits?.length > 0 ||
    hiddenOrganizationUnits.length > 0;

  const onChange = (key, value) => {
    setData((prevData) => {
      const updatedData = { ...prevData };
      update(updatedData, key, (v) => value);
      return updatedData;
    });
    setIsDirty(true);
    setIsEdited(true);
  };

  const [errorMessage, setErrorMessage] = useState(null);

  const mutation = useSaveTotalRewardsPerk();

  const onSubmit = async () => {
    setErrorMessage(null);
    await mutation.mutate(
      {
        tenant: companyId,
        id: data.id,
        title: data.title,
        visibility: !!data.visibility,
        employeeGroups: uniq([...data.employeeGroups, ...hiddenEmployeeGroups]),
        rentableGroups: uniq([...data.rentableGroups, ...hiddenRentableGroups]),
        organizationUnits: uniq([
          ...data.organizationUnits,
          ...hiddenOrganizationUnits,
        ]),
        pointBanks:
          data.isAutomatic && data.sourceType === "pointsBanks"
            ? data.pointBanks
            : [],
        perkSubscriptions:
          data.isAutomatic && data.sourceType === "benefits"
            ? data.perkSubscriptions
            : [],
        isAutomatic: !!data.isAutomatic,
        status: !!data.status,
        activeFrom: data.activeFrom
          ? moment(data.activeFrom).format("YYYY-MM-DD")
          : undefined,
        activeTo: data.activeTo
          ? moment(data.activeTo).format("YYYY-MM-DD")
          : undefined,
        image: data.image || undefined,
        body: data.body,
        shortDescription: data.shortDescription,
        videos: data.videos,
        files: data.files,
        value: data.value,
        sumFormat: data.value ? data.sumFormat : 0,
        category: data.category,
        sort: data.sort || 0,
      },
      {
        onSuccess: (data) => {
          dynamicNotification(__("Pomyślnie zapisano dane"));
          setIsEdited(false, () => {
            if (isNew) {
              history.push(listingUrl);
            }
          });
        },
        onError: (error) => {
          if (error.message) {
            setErrorMessage(error.message);
          }
          dynamicNotification(__("Nie udało się zapisać benefitu"), "error");
        },
      }
    );
  };

  const addVideo = async () => {
    const newVideos = data ? [...data?.videos] : [];
    newVideos.push({ link: "", title: "" });
    onChange("videos", newVideos);
  };

  const removeVideo = async (key) => {
    const newVideos = data ? [...data?.videos] : [];
    newVideos.splice(key, 1);
    onChange("videos", newVideos);
  };

  const addFile = async () => {
    const newFiles = data ? [...data?.files] : [];
    newFiles.push({ title: "", file: null });
    onChange("files", newFiles);
  };

  const removeFile = async (key) => {
    const newFiles = data ? [...data?.files] : [];
    newFiles.splice(key, 1);
    onChange("files", newFiles);
  };

  const valueFormats = useMemo(
    () => [
      {
        value: 0,
        label: "brak formatowania",
        tooltip: __("dowolna wartość słowna, liczbowa lub kropka"),
        disabled: data.isAutomatic || data.isFinancial,
      },
      {
        value: 1,
        label: "liczba po przecinku w formacie 0 000,00",
        disabled: data.isAutomatic || data.isFinancial,
      },
      {
        value: 2,
        label: "liczba całkowita w formacie 0 000",
        disabled: data.isAutomatic || data.isFinancial,
      },
      {
        value: 3,
        label: "liczba PKT w formacie 0 000 pkt",
        disabled: !data.isFinancial,
      },
      {
        value: 4,
        label: "wartość zł w formacie 0 000,00 zł",
        disabled: !data.isFinancial,
      },
      {
        value: 5,
        label: "wartość EUR w formacie 0 000,00 EUR",
        disabled: !data.isFinancial,
      },
    ],
    [data.isAutomatic, data.isFinancial]
  );

  const companyName = useCompanyName();
  return (
    <CSSTransitionGroup
      component="div"
      transitionName="TabsAnimation"
      transitionAppear
      transitionAppearTimeout={0}
      transitionEnter={false}
      transitionLeave={false}
    >
      <PageTitle
        heading={
          isNew
            ? __(`Tworzenie benefitu w firmie {0}`, [companyName])
            : __(`Edycja benefitu (ID: {0}) w firmie {1}`, [
                perkId,
                companyName,
              ])
        }
        breadcrumbsHeading={
          isNew
            ? __("Tworzenie benefitu")
            : `${__("Edycja benefitu")} (ID: ${perkId})`
        }
        breadcrumbs={[
          ...getCompanyBaseBreadcrumbs(companyId, companyName),
          {
            title: "Total Reward Statement",
            link: `${pathPrefix}/total-reward`,
          },
          {
            title: __("Zarządzanie benefitami"),
            link: listingUrl,
          },
        ]}
        pushToHistory={!isNew}
      />
      {errorMessage && <Alert color="danger">{errorMessage}</Alert>}
      <ContentLoading
        show={isLoading}
        messageStyle={{ position: "absolute", top: "20%", right: "50%" }}
      >
        <Form
          id="totalRewardPerkForm"
          data={data}
          config={{
            title: isNew ? (
              __("Tworzenie benefitu")
            ) : (
              <>
                {__("Edycja benefitu")}{" "}
                {data.updated?.at && (
                  <span className="font-size-md pl-3">
                    ({__("Data ostatniej zmiany")}:{" "}
                    {moment(data.updated.at).format("YYYY-MM-DD HH:mm:ss")})
                  </span>
                )}
              </>
            ),
            stickyTitle: true,
            defaultOnChange: onChange,
            onSubmit: () => {
              if (!isImpersonated) onSubmit();
            },
            buttons: [
              {
                size: "lg",
                color: "light",
                className: "mr-2",
                text: "Wróć",
                id: "perkFormBack",
                onClick: () => {
                  history.push(listingUrl);
                },
              },
              {
                size: "lg",
                color: "primary",
                className: "mr-2",
                text: "Zapisz",
                type: "submit",
                id: "perkFormSubmit",
                disabled: isImpersonated || !isDirty,
                permission: totalRewardPerksWrite,
              },
            ],
            formGroups: [
              {
                formElements: [
                  {
                    type: "text",
                    id: "title",
                    label: "Nazwa",
                    translateTitle: false,
                    validation: ["required"],
                    translatable: isNew
                      ? null
                      : {
                          show: true,
                          code: `totalRewardStatement:perkObject:${perkId}:title`,
                          isCms: true,
                        },
                  },
                  {
                    type: "wysiwyg",
                    id: "body",
                    label: "Opis",
                    validation: ["required"],
                    translatable: isNew
                      ? null
                      : {
                          show: true,
                          code: `totalRewardStatement:perkObject:${perkId}:body`,
                          isCms: true,
                        },
                  },
                  {
                    type: "wysiwyg",
                    id: "shortDescription",
                    label: "Krótki opis",
                    props: {
                      showRemaining: true,
                      maxCharCount: 250,
                    },
                    translatable: isNew
                      ? null
                      : {
                          show: true,
                          code: `totalRewardStatement:perkObject:${perkId}:shortDescription`,
                          isCms: true,
                        },
                  },
                  {
                    type: "select",
                    id: "category",
                    label: "Kategoria",
                    options: perkCategoriesOptions
                      ?.filter((o) => o.status)
                      .map((o) => ({
                        label: o.label,
                        value: `/omb/perk-categories/${o.value}`,
                      })),
                    validation: ["required"],
                  },
                  {
                    label:
                      "Okładka. Maksymalny rozmiar pliku - 3MB, sugerowane proporcje obrazka to 16:9, orientacja pozioma.",
                    type: "file",
                    id: "image",
                    onChange: (_, v) => {
                      setCoverImgInfo([]);
                      fileReader.onload = (e) => {
                        const { result } = e.target;
                        if (result) {
                          setFileDataURL(result);
                        }
                      };
                      fileReader.readAsDataURL(v[0]);

                      onChange("image", v[0]);
                      return v;
                    },
                    validation: [
                      data.imageUrl ? undefined : "required",
                      {
                        method: "allowedExtensions",
                        args: [["jpg", "jpeg", "png", "webp"]],
                      },
                    ],
                    value: [data?.image],
                  },
                  {
                    type: "staticValue",
                    id: "showImg",
                    value:
                      data.image || data.imageUrl ? (
                        <div className="container-fluid">
                          <div className="row">
                            <div className="col col-auto">
                              <img
                                src={data.image ? fileDataURL : data.imageUrl}
                                onLoad={onImgLoad}
                                alt=""
                                style={{
                                  borderRadius: "16px",
                                  maxWidth: "254px",
                                  height: "168px",
                                  objectFit: "cover",
                                  aspectRatio: "16 / 9",
                                  alignSelf: "flex-start",
                                }}
                              />
                            </div>
                            <div className="col">
                              <ul>
                                {coverImgInfo.map(({ title, value }) => (
                                  <li key={title}>
                                    {title}: {value}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      ) : null,
                  },
                ],
              },
              {
                title: "Widoczność benefitu",
                formElements: [
                  {
                    type: "boolean",
                    id: "status",
                    label: "Widoczność w zakładce Twoje Benefity",
                    displayCondition: yourBenefitsEnabled,
                  },
                  {
                    type: "boolean",
                    id: "visibility",
                    label: "Widoczność w zakładce Dostępne benefity",
                    displayCondition: availableBenefitsEnabled,
                  },
                  {
                    type: "dateRange",
                    id: "active",
                    label: "Ograniczenie widoczności",
                    displayCondition: availableBenefitsEnabled,
                    props: {
                      minDateFrom: new Date(1970, 1, 1, 1),
                      minDateTo: new Date(1970, 1, 1, 1),
                    },
                    onChange: (key, value) => {
                      const { from, to } = value;
                      onChange(`${key}From`, from);
                      onChange(`${key}To`, to);
                    },
                  },
                  {
                    type: "staticValue",
                    id: "anyGroupSelected",
                    label:
                      "Wybierz grupy pracownicze i/lub grupy dochodowości i/lub jednostki organizacyjne. *",
                    tooltip: {
                      type: "info",
                      content: __(
                        "Jeśli chcesz aby benefit był widoczny dla wszystkich pracowników, wskaż wszystkie grupy pracownicze, a grupy dochodowości i jednostki organizacyjne pozostaw puste."
                      ),
                    },
                    displayCondition: availableBenefitsEnabled,
                    validation: data.visibility
                      ? [
                          {
                            method: "customValidation",
                            args: [
                              () => {
                                if (anyGroupSelected) {
                                  return false;
                                }
                                return __(
                                  "Wybór przynajmniej jednej opcji jest wymagany."
                                );
                              },
                            ],
                          },
                        ]
                      : undefined,
                  },
                  {
                    type: "autocompleteMultiselect",
                    id: "employeeGroups",
                    label: "Grupa pracownicza",
                    displayCondition: availableBenefitsEnabled,
                    options: employeeGroups,
                    onChange: (key, value) => {
                      onChange(key, value);
                      onChange("anyGroupSelected", false);
                    },
                    props: {
                      closeMenuOnSelect: false,
                    },
                  },
                  {
                    type: "autocompleteMultiselect",
                    id: "rentableGroups",
                    label: "Grupa dochodowości",
                    displayCondition: availableBenefitsEnabled,
                    options: rentableGroups,
                    onChange: (key, value) => {
                      onChange(key, value);
                      onChange("anyGroupSelected", false);
                    },
                    props: {
                      closeMenuOnSelect: false,
                    },
                  },
                  {
                    type: "autocompleteMultiselect",
                    id: "organizationUnits",
                    label: "Jednostki organizacyjne",
                    displayCondition: availableBenefitsEnabled,
                    options: organizationUnits,
                    onChange: (key, value) => {
                      onChange(key, value);
                      onChange("anyGroupSelected", false);
                    },
                    props: {
                      closeMenuOnSelect: false,
                    },
                  },
                ],
              },
              automationEnabled
                ? {
                    title: "Ustawienia źródeł danych",
                    formElements: [
                      {
                        type: "boolean",
                        id: "isAutomatic",
                        label: "Benefit automatyczny",
                        onChange: (key, value) => {
                          onChange(key, value);
                          if (data.sumFormat < 3) {
                            onChange("value", true);
                            onChange("sumFormat", "3");
                          }
                          if (value) {
                            onChange("isFinancial", true);
                          }
                        },
                      },
                      {
                        type: "select",
                        id: "sourceType",
                        label: "Typ źródła",
                        options: translateLabels(
                          sourceTypes.filter(
                            (s) =>
                              (s.enabledImportBenefits &&
                                enabledImportBenefits) ||
                              (s.enabledImportPointsBanks &&
                                enabledImportPointsBanks)
                          )
                        ),
                        validation: data.isAutomatic ? ["required"] : undefined,
                        depends: {
                          value: true,
                          field: "isAutomatic",
                        },
                      },
                      {
                        type: "autocompleteMultiselect",
                        id: "pointBanks",
                        label: "Banki punktów",
                        options: pointsBanks?.filter(({ value }) =>
                          enabledPointsBanks.includes(value)
                        ),
                        validation: data.isAutomatic ? ["required"] : undefined,
                        depends: {
                          value: "pointsBanks",
                          field: "sourceType",
                        },
                        props: {
                          closeMenuOnSelect: false,
                        },
                      },
                      {
                        type: "autocompleteMultiselect",
                        id: "perkSubscriptions",
                        label: "Świadczenia abonamentowe",
                        options: allBenefits?.filter(({ value }) =>
                          enabledBenefits.includes(value)
                        ),
                        validation: data.isAutomatic ? ["required"] : undefined,
                        depends: {
                          value: "benefits",
                          field: "sourceType",
                        },
                        props: {
                          closeMenuOnSelect: false,
                        },
                      },
                    ],
                  }
                : undefined,
              {
                title: "Video",
                formElements: [
                  ...(data?.videos?.length > 0
                    ? Array.from(Array(data?.videos?.length).keys()).map(
                        (k) => ({
                          layout: LAYOUT_TWO_COLUMNS,
                          formElements: [
                            {
                              type: "text",
                              id: `videos[${k}].title`,
                              label: "Tytuł Video",
                              value: data?.videos[k].title,
                              validation: ["required"],
                            },
                            {
                              type: "text",
                              id: `videos[${k}].link`,
                              label: "Link do Video",
                              value: data?.videos[k].link,
                              validation: ["required"],
                            },
                            {
                              type: "button",
                              label: "Usuń Video",
                              props: {
                                outline: true,
                                size: "sm",
                                className: "mb-4",
                              },
                              onChange: () => removeVideo(k),
                            },
                          ],
                        })
                      )
                    : []),
                  {
                    type: "hr",
                  },
                  {
                    type: "button",
                    label: "Dodaj Video",
                    onChange: addVideo,
                  },
                ],
              },
              {
                title: __("Pliki"),
                formElements: [
                  ...(data?.files?.length > 0
                    ? Array.from(Array(data?.files?.length).keys()).map(
                        (k) => ({
                          layout: LAYOUT_TWO_COLUMNS,
                          formElements: [
                            {
                              type: "text",
                              id: `files[${k}].title`,
                              label: "Skrócona nazwa pliku",
                              value: data?.files[k].title,
                              validation: ["required"],
                            },
                            {
                              type: "file",
                              id: `files[${k}].file`,
                              label: "Plik",
                              value: data?.files[k].file,
                              validation: data?.files[k].fileUrl
                                ? undefined
                                : ["required"],
                            },
                            {
                              type: "button",
                              label: "Usuń Plik",
                              props: {
                                outline: true,
                                size: "sm",
                                className: "mb-4",
                              },
                              onChange: () => removeFile(k),
                            },
                            {
                              component: (
                                <>
                                  {data?.files[k].fileUrl ? (
                                    <a
                                      href={data.files[k].fileUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="btn btn-sm btn-outline-primary"
                                    >
                                      {__("Pobierz")}
                                    </a>
                                  ) : (
                                    ""
                                  )}
                                </>
                              ),
                            },
                          ],
                        })
                      )
                    : []),
                  {
                    type: "hr",
                  },
                  {
                    type: "button",
                    label: "Dodaj Plik",
                    onChange: addFile,
                  },
                ],
              },
              {
                title: __("Pozostałe ustawienia"),
                formElements: [
                  {
                    type: "boolean",
                    id: "isFinancial",
                    label: "Benefit finansowy",
                    props: {
                      disabled: data.isAutomatic,
                    },
                    onChange: (key, value) => {
                      onChange("isFinancial", value);
                      if (value) {
                        onChange("value", value);
                        if (data.sumFormat < 3) {
                          onChange("sumFormat", "3");
                        }
                      } else if (data.sumFormat > 2) {
                        onChange("value", false);
                        onChange("sumFormat", "0");
                      }
                    },
                  },
                  {
                    type: "radio",
                    id: "sumFormat",
                    label: "Waluta i formatowanie wartości",
                    options: translateLabels(valueFormats),
                    onChange: (key, value) => {
                      onChange("value", value > 0);
                      onChange("sumFormat", value);
                    },
                  },
                  {
                    type: "number",
                    id: "sort",
                    label: "Kolejność wyświetlania",
                    validation: [{ method: "greaterEqualThan", args: [0] }],
                  },
                ],
              },
            ],
          }}
        />
      </ContentLoading>
    </CSSTransitionGroup>
  );
}

const sourceTypes = [
  {
    value: "pointsBanks",
    label: "Banki punktów",
    enabledImportPointsBanks: true,
  },
  {
    value: "benefits",
    label: "Świadczenia abonamentowe",
    enabledImportBenefits: true,
  },
];

TotalRewardPerksForm.propTypes = {
  perkId: PropTypes.string.isRequired,
  pathPrefix: PropTypes.string.isRequired,
  yourBenefitsEnabled: PropTypes.bool,
  availableBenefitsEnabled: PropTypes.bool,
  enabledImportPointsBanks: PropTypes.bool,
  enabledImportBenefits: PropTypes.bool,
  enabledPointsBanks: PropTypes.array,
  enabledBenefits: PropTypes.array,
  setIsEdited: PropTypes.func,
};

TotalRewardPerksForm.defaultProps = {
  yourBenefitsEnabled: false,
  availableBenefitsEnabled: false,
  enabledImportPointsBanks: false,
  enabledImportBenefits: false,
  enabledPointsBanks: [],
  enabledBenefits: [],
  setIsEdited: () => {},
};
